// AuthContext + axios bootstrapping for the ZAMBA app.
// Bearer-token approach: token lives in sessionStorage (cleared on tab close,
// not accessible cross-origin). Full fix would use HttpOnly cookies + CSRF token.

import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "zamba_token";

// -------- axios global interceptors ----------
let _interceptorInstalled = false;
function installInterceptor() {
  if (_interceptorInstalled) return;
  // REQUEST: attach Bearer token from sessionStorage to every call.
  axios.interceptors.request.use((config) => {
    const t = sessionStorage.getItem(TOKEN_KEY);
    if (t && !config.headers?.Authorization) {
      config.headers = { ...(config.headers || {}), Authorization: `Bearer ${t}` };
    }
    return config;
  });
  // RESPONSE: detect 401 (token expired / invalid / missing) on any API call,
  // clear the stale token, and broadcast a "session-expired" event. App.js
  // listens for this event and pops a one-shot modal that lets the user log
  // back in WITHOUT a page reload — so the in-memory wizard state survives,
  // they re-auth, and can immediately retry the failed action (Save, PDF,
  // schedule export, etc.).
  axios.interceptors.response.use(
    (resp) => resp,
    (err) => {
      const status = err?.response?.status;
      // Don't fire the expired-session signal on /auth/login or /auth/register
      // — a 401 there means "wrong password", not "session expired".
      const url = err?.config?.url || "";
      const isAuthEndpoint =
        url.includes("/auth/login") ||
        url.includes("/auth/register") ||
        url.includes("/auth/forgot-password") ||
        url.includes("/auth/reset-password");
      if (status === 401 && !isAuthEndpoint && sessionStorage.getItem(TOKEN_KEY)) {
        sessionStorage.removeItem(TOKEN_KEY);
        try {
          window.dispatchEvent(new CustomEvent("zamba:session-expired"));
        } catch (_) { /* ignore */ }
      }
      return Promise.reject(err);
    }
  );
  _interceptorInstalled = true;
}
installInterceptor();

// -------- helpers ----------
export function formatApiError(err) {
  const detail = err?.response?.data?.detail;
  if (detail == null) return err?.message || "Network error";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" • ");
  }
  if (typeof detail === "object" && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export function getStoredToken() {
  return sessionStorage.getItem(TOKEN_KEY);
}

// -------- Context ----------
const AuthContext = createContext({
  user: null,
  loading: true,
  login: async () => {},
  register: async () => {},
  logout: () => {},
});

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  // `sessionExpired` flag is read by App.js to render the inline re-login
  // modal when the axios response interceptor catches a 401 mid-session.
  const [sessionExpired, setSessionExpired] = useState(false);

  // On mount, if we have a token, validate it via /api/auth/me
  useEffect(() => {
    let cancelled = false;
    const t = getStoredToken();
    if (!t) {
      setLoading(false);
      return;
    }
    axios
      .get(`${API}/auth/me`)
      .then((r) => !cancelled && setUser(r.data))
      .catch(() => {
        if (cancelled) return;
        sessionStorage.removeItem(TOKEN_KEY);
        setUser(null);
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, []);

  // Listen for the global "session-expired" event broadcast by the axios
  // response interceptor (lib/auth.js). When it fires, clear the user state
  // and flip the sessionExpired flag so App.js can render the re-login
  // modal — wizard state stays in memory, the user re-authenticates, and
  // they can immediately retry the action that triggered the 401.
  useEffect(() => {
    const onExpired = () => {
      setUser(null);
      setSessionExpired(true);
    };
    window.addEventListener("zamba:session-expired", onExpired);
    return () => window.removeEventListener("zamba:session-expired", onExpired);
  }, []);

  const login = useCallback(async (email, password) => {
    const { data } = await axios.post(`${API}/auth/login`, { email, password });
    sessionStorage.setItem(TOKEN_KEY, data.token);
    setUser(data.user);
    setSessionExpired(false);
    return data.user;
  }, []);

  const register = useCallback(async (email, password, name) => {
    const { data } = await axios.post(`${API}/auth/register`, { email, password, name });
    // Registration no longer auto-signs in — user must verify email first.
    return data;
  }, []);

  const logout = useCallback(() => {
    sessionStorage.removeItem(TOKEN_KEY);
    setUser(null);
    // Best-effort backend ping (stateless JWT — no real server-side action needed)
    axios.post(`${API}/auth/logout`).catch(() => {});
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, sessionExpired }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}

// -------- Auth API helpers ----------
export const authApi = {
  requestPasswordReset: (email) =>
    axios.post(`${API}/auth/forgot-password`, { email }),
  resetPassword: (token, new_password) =>
    axios.post(`${API}/auth/reset-password`, { token, new_password }),
};

// -------- Project API helpers ----------
export const projectApi = {
  list: () => axios.get(`${API}/projects`).then((r) => r.data),
  get: (id) => axios.get(`${API}/projects/${id}`).then((r) => r.data),
  create: (name, payload) =>
    axios.post(`${API}/projects`, { name, payload }).then((r) => r.data),
  update: (id, name, payload) =>
    axios.put(`${API}/projects/${id}`, { name, payload }).then((r) => r.data),
  remove: (id) => axios.delete(`${API}/projects/${id}`).then((r) => r.data),
  clone: (id) => axios.post(`${API}/projects/${id}/clone`).then((r) => r.data),
};
